import { useState } from "react";
import { emojis } from "@tiptap/extension-emoji";
import { Input, Popover } from "@tellescope/design-system";
import { Button } from "../button/button";
import { Smile } from "lucide-react";
import { levenshteinDistance } from "@tellescope/utils";
import { useTranslation } from "react-i18next";

export interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
  triggerButton?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const EmojiPicker = ({
  onEmojiSelect,
  triggerButton,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: EmojiPickerProps) => {
  const { t } = useTranslation("emojis");
  const [internalOpen, setInternalOpen] = useState(false);
  const [emojiSearch, setEmojiSearch] = useState("");

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? controlledOnOpenChange || (() => {}) : setInternalOpen;

  const handleEmojiSelect = (emoji: string) => {
    onEmojiSelect(emoji);
    setOpen(false);
    setEmojiSearch("");
  };

  // Filter out single-character symbols/letters when browsing (not searching)
  const isSymbolOrLetter = (emoji: (typeof emojis)[0]) => {
    // Filter out: single letters, numbers, symbols, keycaps, flags
    const unwantedPatterns = [
      /^[a-zA-Z0-9]$/, // Single letters/numbers
      /^[#*]$/, // Hash and asterisk
      /keycap/i, // Keycap emojis
      /^(regional_indicator|flag:)/i, // Flag emojis
      /^(left arrow|right arrow|up arrow|down arrow)/i, // Arrow symbols
      /^(heavy|white|black) (plus|minus|division|multiplication)/i, // Math symbols
    ];
    return unwantedPatterns.some((pattern) => pattern.test(emoji.name));
  };

  const filteredEmojis = emojiSearch
    ? [...emojis].sort((a, b) => {
        const lowerSearch = emojiSearch.toLowerCase();
        const aSearchStrings = [
          a.name.toLowerCase(),
          ...a.tags.map((val) => val.toLowerCase()),
          ...a.shortcodes.map((val) => val.toLowerCase()),
        ].filter((item) => item.includes(lowerSearch));
        const bSearchStrings = [
          b.name.toLowerCase(),
          ...b.tags.map((val) => val.toLowerCase()),
          ...b.shortcodes.map((val) => val.toLowerCase()),
        ].filter((item) => item.includes(lowerSearch));
        const aMinDistance = Math.min(...aSearchStrings.map((str) => levenshteinDistance(lowerSearch, str)));
        const bMinDistance = Math.min(...bSearchStrings.map((str) => levenshteinDistance(lowerSearch, str)));
        const bIsRegionalIndicator =
          b.name.includes("regional_indicator") &&
          !a.name.includes("regional_indicator") &&
          levenshteinDistance(lowerSearch, "region") > 5;
        if (bIsRegionalIndicator) {
          return -1;
        }
        return aMinDistance - bMinDistance;
      })
    : emojis.filter((emoji) => !isSymbolOrLetter(emoji));

  // Show more emojis when searching, fewer when browsing all
  const displayedEmojis = emojiSearch ? filteredEmojis.slice(0, 100) : filteredEmojis.slice(0, 200);

  const defaultTrigger = (
    <Button variant="ghost" size="sm" aria-label="Insert emoji" type="button">
      <Smile className="h-4 w-4" />
    </Button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>{triggerButton || defaultTrigger}</Popover.Trigger>
      <Popover.Content className="w-80">
        <div className="grid gap-4">
          <div className="space-y-2">
            <h4 className="font-medium leading-none">{t("Insert Emoji")}</h4>
            <p className="text-sm text-muted-foreground">{t("Search and select an emoji")}</p>
          </div>
          <div className="grid gap-2">
            <Input
              placeholder="Search emojis..."
              value={emojiSearch}
              onChange={(e) => setEmojiSearch(e.target.value)}
              autoFocus
            />
            <div
              className="grid grid-cols-8 gap-1 max-h-60 overflow-y-auto p-2 border rounded"
              onWheelCapture={(e) => e.stopPropagation()}
            >
              {displayedEmojis.map((emoji) => (
                <button
                  key={emoji.name}
                  type="button"
                  className="text-2xl hover:bg-accent rounded p-1 transition-colors"
                  onClick={() => {
                    if (emoji.emoji) {
                      handleEmojiSelect(emoji.emoji);
                    }
                  }}
                  title={emoji.name}
                >
                  {emoji.emoji}
                </button>
              ))}
              {displayedEmojis.length === 0 && (
                <div className="col-span-8 text-center text-sm text-muted-foreground py-4">{t("No emojis found")}</div>
              )}
              {!emojiSearch && filteredEmojis.length > displayedEmojis.length && (
                <div className="col-span-8 text-center text-xs text-muted-foreground py-2 border-t mt-2 pt-2">
                  {t("Showing {{showCount}} of {{count}} emojis. Search to find more!", {
                    count: filteredEmojis.length,
                    showCount: displayedEmojis.length,
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </Popover.Content>
    </Popover>
  );
};
